import { useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { motion } from 'motion/react'
import { X } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useImageBackground } from '@/hooks/useImageBackground'
import { useAuth } from '@/hooks/useAuth'

// Ultime instance: se il database non risponde (preview appena avviato, rete
// intermittente), il carosello mostra comunque queste immagini.
const FALLBACK_IMAGES: string[] = [
  'https://tqqcgdbwrboghrrkyvnf.supabase.co/storage/v1/object/public/homepage-gallery/fallback/c20e877c-907e-4107-8ec3-23ef5ebb4089-Cavallini.jpg',
  'https://tqqcgdbwrboghrrkyvnf.supabase.co/storage/v1/object/public/homepage-gallery/fallback/3789959b-257c-4925-b73d-6e976fb9d004-Tiffany.webp',
  'https://tqqcgdbwrboghrrkyvnf.supabase.co/storage/v1/object/public/homepage-gallery/fallback/e306f7d9-196d-43ae-bfb6-8195c204e55f-Cappellino-Yankee.jpg',
  'https://tqqcgdbwrboghrrkyvnf.supabase.co/storage/v1/object/public/homepage-gallery/fallback/9a799997-b20f-4089-98cf-486f302c4def-DW-watch.webp',
  'https://tqqcgdbwrboghrrkyvnf.supabase.co/storage/v1/object/public/homepage-gallery/fallback/c3939bd2-3b8b-4f60-aa01-3c22979a9fc7-Prada.avif',
  'https://tqqcgdbwrboghrrkyvnf.supabase.co/storage/v1/object/public/homepage-gallery/fallback/366d2797-a6e5-497d-9be6-9adc0f3d089a-Harala-Denim.webp',
  'https://tqqcgdbwrboghrrkyvnf.supabase.co/storage/v1/object/public/homepage-gallery/fallback/ce03456f-0972-450a-911d-7e07f8e4ac4c-Waldor.webp',
  'https://tqqcgdbwrboghrrkyvnf.supabase.co/storage/v1/object/public/homepage-gallery/fallback/f078680c-afff-44a8-bd0e-072a5652f431-Zaino.webp',
  'https://tqqcgdbwrboghrrkyvnf.supabase.co/storage/v1/object/public/homepage-gallery/fallback/79d290b1-fb86-47d8-a152-c0411e94e854-Yepoda.jpg',
  'https://tqqcgdbwrboghrrkyvnf.supabase.co/storage/v1/object/public/homepage-gallery/fallback/e5a31126-87f8-435c-b8c1-0fa42edcd661-Zara-Parfume.jpg',
]

const SPEED = 0.8

interface MarqueeItem {
  id?: string
  src: string
  title: string
  category?: string
}

export default function ImageMarquee() {
  const trackRef = useRef<HTMLDivElement>(null)
  const [grabbing, setGrabbing] = useState(false)
  const [items, setItems] = useState<MarqueeItem[]>([])
  const [imagesLoaded, setImagesLoaded] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const { user } = useAuth()
  const state = useRef({
    offset: 0,
    dragging: false,
    velocity: 0,
    startX: 0,
    startOffset: 0,
    lastX: 0,
    lastT: 0,
  })
  const hoveredRef = useRef(false)

  useEffect(() => {
    if (!user) { setIsAdmin(false); return }
    supabase
      .rpc('has_role', { _user_id: user.id, _role: 'admin' })
      .then(({ data }) => setIsAdmin(!!data))
  }, [user?.id])

  const handleHideItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id))
  }, [])

  useEffect(() => {
    let cancelled = false

    async function loadItems(retry = true) {
      try {
        const sb = supabase as any
        const [settingsRes, fallbackRes] = await Promise.all([
          sb.from('homepage_gallery_settings').select('prioritize_real_images').eq('id', 1).maybeSingle(),
          sb.from('homepage_fallback_images').select('image_url').order('position', { ascending: true }),
        ])
        const fallbackItems: MarqueeItem[] = (fallbackRes.data ?? []).map((r: { image_url: string }) => ({
          src: r.image_url,
          title: '',
        }))
        // Se una delle due query fallisce (rete intermittente), non fidarci del
        // default: riprova invece di mostrare un carosello vuoto.
        if ((settingsRes.error || fallbackRes.error) && retry) {
          setTimeout(() => { if (!cancelled) loadItems(false) }, 1500)
          return
        }
        const prioritizeReal: boolean = settingsRes.data?.prioritize_real_images ?? true
        if (!prioritizeReal && fallbackItems.length > 0) {
          if (!cancelled) setItems(fallbackItems)
          return
        }

        const { data: carouselData, error: carouselError } = await supabase
          .from('carousel_images')
          .select('image_url, title, category')
          .eq('is_active', true)
          .order('position', { ascending: true })
          .limit(40)

        if (!cancelled && !carouselError && carouselData && carouselData.length > 0) {
          const carouselItems: MarqueeItem[] = carouselData.map((row) => ({
            src: row.image_url,
            title: row.title || '',
            category: row.category ?? undefined,
          }))
          if (cancelled) return
          setItems(carouselItems)
          return
        }

        const { data, error } = await supabase
          .rpc('get_public_carousel_items')

        if (cancelled) return
        if (error || !data || data.length === 0) {
          if (fallbackItems.length > 0) {
            setItems(fallbackItems)
          } else if (retry) {
            setTimeout(() => { if (!cancelled) loadItems(false) }, 1500)
          }
          return
        }

        const realItems: MarqueeItem[] = data.map((row) => ({
          id: row.id,
          src: row.image_url!,
          title: row.title,
          category: row.category ?? undefined,
        }))

        const interleaved = interleaveByCategory(realItems).slice(0, 20)

        if (cancelled) return
        setItems(interleaved)
      } catch {
        if (retry) {
          setTimeout(() => { if (!cancelled) loadItems(false) }, 1500)
        }
      }
    }

    loadItems()
    return () => {
      cancelled = true
    }
  }, [])

  const displayItems: MarqueeItem[] =
    items.length > 0
      ? items
      : FALLBACK_IMAGES.map((src) => ({ src, title: '' }))

  // Preload all images before showing the marquee
  useEffect(() => {
    if (displayItems.length === 0) return
    let cancelled = false
    let remaining = displayItems.length

    function checkDone() {
      remaining--
      if (remaining <= 0 && !cancelled) setImagesLoaded(true)
    }

    displayItems.forEach((item) => {
      const img = new Image()
      img.onload = checkDone
      img.onerror = checkDone
      img.src = item.src
    })

    // Fallback: show after 5s even if some images haven't loaded
    const timeout = setTimeout(() => {
      if (!cancelled) setImagesLoaded(true)
    }, 5000)

    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [displayItems])

  // Duplicate for seamless loop
  const loopItems = [...displayItems, ...displayItems]

  useEffect(() => {
    let raf = 0
    const tick = () => {
      const track = trackRef.current
      const s = state.current
      if (track) {
        if (!s.dragging && !hoveredRef.current) {
          if (Math.abs(s.velocity) > 0.1) {
            s.offset += s.velocity
            s.velocity *= 0.95
          } else {
            s.velocity = 0
            s.offset -= SPEED
          }
        }
        const halfWidth = track.scrollWidth / 2
        if (halfWidth > 0) {
          if (s.offset <= -halfWidth) s.offset += halfWidth
          if (s.offset > 0) s.offset -= halfWidth
        }
        track.style.transform = `translate3d(${s.offset}px, 0, 0)`
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [displayItems.length])

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const s = state.current
    s.dragging = true
    s.velocity = 0
    s.startX = e.clientX
    s.startOffset = s.offset
    s.lastX = e.clientX
    s.lastT = performance.now()
    setGrabbing(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const s = state.current
    if (!s.dragging) return
    const now = performance.now()
    const dt = now - s.lastT || 16
    s.velocity = ((e.clientX - s.lastX) / dt) * 16
    s.lastX = e.clientX
    s.lastT = now
    s.offset = s.startOffset + (e.clientX - s.startX)
  }

  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const s = state.current
    if (!s.dragging) return
    s.dragging = false
    setGrabbing(false)
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }

  return (
    <section className="relative py-4 overflow-hidden">
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-[80px] sm:h-[100px] z-10">
        <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="w-full h-full">
          <path style={{ fill: '#F5F7F9' }} d="M0 0H1440V50C1440 50 1200 100 720 100C240 100 0 50 0 50V0Z" />
        </svg>
      </div>
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-[80px] sm:h-[100px] z-10">
        <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="w-full h-full">
          <path style={{ fill: '#F5F7F9' }} d="M0 100H1440V50C1440 50 1200 0 720 0C240 0 0 50 0 50V100Z" />
        </svg>
      </div>

      <div
          ref={trackRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onMouseEnter={() => { hoveredRef.current = true }}
          onMouseLeave={() => { hoveredRef.current = false }}
          className={`flex w-max gap-3 sm:gap-4 py-4 select-none ${grabbing ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{ willChange: 'transform', touchAction: 'pan-y' }}
        >
          {loopItems.map((item, i) => (
            <MarqueeCard key={`${item.src}-${i}`} item={item} isAdmin={isAdmin} onHide={handleHideItem} />
          ))}
        </div>
    </section>
  )
}

function MarqueeCard({ item, isAdmin, onHide }: { item: MarqueeItem; isAdmin: boolean; onHide: (id: string) => void }) {
  const bg = useImageBackground(item.src)
  const [confirming, setConfirming] = useState(false)

  const handleHide = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!item.id) return
    const { error } = await supabase
      .from('wishlist_items')
      .update({ hidden_from_carousel: true })
      .eq('id', item.id)
    if (!error) onHide(item.id)
  }

  return (
    <div
      className="w-60 h-80 sm:w-80 sm:h-[32rem] flex-shrink-0 rounded-2xl overflow-hidden relative group"
      style={{ backgroundColor: bg }}
    >
      <img
        src={item.src}
        alt={item.title || ''}
        loading="lazy"
        draggable={false}
        className="w-full h-full object-contain pointer-events-none"
      />
      {isAdmin && item.id && !confirming && (
        <button
          onPointerDown={(e) => { e.stopPropagation() }}
          onPointerUp={(e) => { e.stopPropagation() }}
          onClick={(e) => { e.stopPropagation(); setConfirming(true) }}
          title="Nascondi dal carosello"
          className="absolute left-1/2 -translate-x-1/2 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity hover:bg-black/70 backdrop-blur-sm"
          style={{ bottom: '70px', touchAction: 'none' }}
        >
          <X className="h-5 w-5" />
        </button>
      )}
      {confirming && (
        <div
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onPointerMove={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="absolute inset-0 z-30 flex items-center justify-center bg-black/40"
          style={{ touchAction: 'none' }}
        >
          <div className="mx-3 rounded-2xl bg-white/95 p-4 text-center shadow-xl backdrop-blur">
            <p className="mb-3 text-sm font-semibold text-neutral-900">
              Eliminare dal carosello?
            </p>
            <div className="flex items-center justify-center gap-2">
              <button
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
                onClick={handleHide}
                className="inline-flex min-h-[44px] items-center justify-center rounded-full bg-neutral-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-neutral-800"
              >
                Sì, elimina
              </button>
              <button
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); setConfirming(false) }}
                className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-neutral-200 px-4 py-2 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-100"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Reorder items so no two adjacent items share the same category.
 * Uses a greedy round-robin approach: always pick the most frequent
 * remaining category that differs from the last placed item.
 */
function interleaveByCategory(items: MarqueeItem[]): MarqueeItem[] {
  if (items.length <= 1) return items

  // Group items by category
  const groups = new Map<string, MarqueeItem[]>()
  for (const item of items) {
    const cat = item.category || 'altro'
    const arr = groups.get(cat)
    if (arr) arr.push(item)
    else groups.set(cat, [item])
  }

  // Sort groups by size descending (largest first)
  const sortedGroups = [...groups.entries()].sort((a, b) => b[1].length - a[1].length)

  const result: MarqueeItem[] = []
  let lastCategory = ''

  while (result.length < items.length) {
    // Find the largest group that isn't the same category as the last placed item
    let picked = -1
    for (let i = 0; i < sortedGroups.length; i++) {
      if (sortedGroups[i][1].length > 0 && sortedGroups[i][0] !== lastCategory) {
        picked = i
        break
      }
    }

    // If no different category available, just take from the largest remaining
    if (picked === -1) {
      for (let i = 0; i < sortedGroups.length; i++) {
        if (sortedGroups[i][1].length > 0) {
          picked = i
          break
        }
      }
    }

    if (picked === -1) break

    const [cat, arr] = sortedGroups[picked]
    result.push(arr.shift()!)
    lastCategory = cat

    // Re-sort by remaining size to keep the round-robin balanced
    sortedGroups.sort((a, b) => b[1].length - a[1].length)
  }

  return result
}
